import { useState } from 'react';
import { Trophy } from 'lucide-react';
import { BADGES } from '@xdface-vault/shared';
import { BadgeCard } from '../features/gamification/components/BadgeCard.js';
import { useBadges } from '../features/gamification/hooks/useGamification.js';
import { ErrorState } from '../components/ui/ErrorState.js';

type Tab = 'all' | 'unlocked' | 'locked';

const VISIBLE_BADGES = BADGES.filter((badge) => badge.condition.metric !== 'notes_created');

export function Badges() {
  const [tab, setTab] = useState<Tab>('all');

  const { data: userBadges, isLoading, isError, refetch } = useBadges();

  const unlockedMap = new Map((userBadges ?? []).map((b) => [b.badgeId, b.unlockedAt]));

  const filtered = VISIBLE_BADGES.filter((b) => {
    if (tab === 'unlocked') return unlockedMap.has(b.id);
    if (tab === 'locked') return !unlockedMap.has(b.id);
    return true;
  });

  const unlockedCount = VISIBLE_BADGES.filter((badge) => unlockedMap.has(badge.id)).length;
  const totalCount = VISIBLE_BADGES.length;
  const progressPct = totalCount ? Math.round((unlockedCount / totalCount) * 100) : 0;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'all', label: 'Todos' },
    { id: 'unlocked', label: 'Desbloqueados' },
    { id: 'locked', label: 'Por desbloquear' },
  ];

  return (
    <div className="page-enter mx-auto max-w-5xl space-y-8 pb-12">
      {/* Brand Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 px-1">
        <div>
          <h1 className="vault-gradient-text text-3xl font-black tracking-tight">Mis Logros</h1>
          <p className="mt-1 text-sm text-text-muted">
            Desbloquea insignias mientras exploras el vault.
          </p>
        </div>

        {/* Brand Progress Bar */}
        <div className="w-full md:w-64 space-y-1.5">
          <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-text-muted">
            <span>Progreso total</span>
            <span className="text-xd-action-end">{progressPct}%</span>
          </div>
          <div className="xd-progress-track h-2.5 rounded-full border border-border overflow-hidden">
            <div
              className="h-full rounded-full bg-xd-action shadow-sm transition-all duration-1000"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Stats Quick Cards */}
      <div className="grid grid-cols-2 gap-4 px-1">
        <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
          <span className="block text-xl font-black text-text">{unlockedCount}</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Obtenidos
          </span>
        </div>
        <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
          <span className="block text-xl font-black text-text">{totalCount - unlockedCount}</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Pendientes
          </span>
        </div>
      </div>

      {/* System Tabs Filter */}
      <div className="flex items-center justify-between gap-4 border-b border-border/50 pb-2 px-1">
        <div className="flex gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={[
                'rounded-lg px-4 py-2 text-xs font-bold transition-all',
                tab === t.id
                  ? 'bg-xd-action text-xd-action-fg shadow-md'
                  : 'text-text-muted hover:text-text hover:bg-surface-2',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Badge grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4 px-1">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-52 rounded-2xl skeleton border border-border" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          title="No se pudieron cargar tus logros"
          description="Revisá tu conexión e intentá de nuevo."
          onRetry={() => refetch()}
        />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center rounded-3xl border border-dashed border-border bg-surface-2/30">
          <Trophy className="h-10 w-10 text-text-muted opacity-20 mb-3" />
          <h3 className="text-sm font-bold text-text">No hay nada aquí</h3>
          <p className="text-xs text-text-muted max-w-xs mt-1">
            Continúa usando el sistema para desbloquear nuevos hitos.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4 px-1">
          {filtered.map((badge) => (
            <BadgeCard
              key={badge.id}
              badge={badge}
              unlocked={unlockedMap.has(badge.id)}
              unlockedAt={unlockedMap.get(badge.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
